import { useEffect } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
import * as Sentry from '@sentry/react';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requireAdmin?: boolean;
}

export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { user, isAdmin, isModerator, isActive, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Prevent console logging of auth details in production
    if (process.env.NODE_ENV === 'development') {
      console.log('[ProtectedRoute] Auth status:', {
        hasUser: !!user,
        isAdmin,
        isModerator,
        isActive,
        loading,
        path: location.pathname
      });
    }
  }, [user, isAdmin, isModerator, isActive, loading, location.pathname]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Check if user is active
  if (!isActive) {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: `Acesso negado (conta inativa) em ${location.pathname}`,
      level: 'warning',
    });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="text-center space-y-4 max-w-md mx-auto p-8">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
            <span className="text-3xl">🔒</span>
          </div>
          <h1 className="text-2xl font-bold">Conta Desativada</h1>
          <p className="text-muted-foreground">
            Sua conta foi desativada. Entre em contato com um administrador.
          </p>
        </div>
      </div>
    );
  }

  // Check if user has at least moderator access
  if (!isAdmin && !isModerator) {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: `Acesso negado (sem permissão de moderador) em ${location.pathname}`,
      level: 'warning',
    });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="text-center space-y-4 max-w-md mx-auto p-8">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
            <span className="text-3xl">🔒</span>
          </div>
          <h1 className="text-2xl font-bold">Acesso Negado</h1>
          <p className="text-muted-foreground">
            Você não tem permissão para acessar esta área.
            Apenas administradores e moderadores podem acessar o painel administrativo.
          </p>
        </div>
      </div>
    );
  }

  // Check admin role if required (for admin-only pages)
  if (requireAdmin && !isAdmin) {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: `Acesso negado (requer admin) em ${location.pathname}`,
      level: 'warning',
    });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="text-center space-y-4 max-w-md mx-auto p-8">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
            <span className="text-3xl">🔒</span>
          </div>
          <h1 className="text-2xl font-bold">Acesso Restrito</h1>
          <p className="text-muted-foreground">
            Esta área é restrita apenas para administradores.
          </p>
        </div>
      </div>
    );
  }

  return <>{children || <Outlet />}</>;
}
